import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Badge,
  Spinner,
  SimpleGrid,
  Card,
  CardBody,
  Flex,
  Icon,
  Tag,
  TagLabel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useColorModeValue,
  List,
  ListItem,
  ListIcon,
} from "@chakra-ui/react";
import { CheckCircleIcon, TimeIcon, StarIcon } from "@chakra-ui/icons";
import workflowRegistry from "../services/WorkflowRegistry";

const AdminWorkflows = ({ standalone = false }) => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const Wrapper = React.Fragment;

  useEffect(() => {
    try {
      setLoading(true);
      const allWorkflows = workflowRegistry.getAllWorkflows();
      setWorkflows(allWorkflows);
    } catch (error) {
      console.error("Error loading workflows:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  if (loading) return (
    <Wrapper>
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Spinner size="xl" />
      </Box>
    </Wrapper>
  );

  const getWorkflowTypeColor = (workflowId) => {
    const colors = {
      'design_strategy': 'blue',
      'design_sprint': 'green',
      'user_research': 'purple',
      'plan_viewer': 'orange'
    };
    return colors[workflowId] || 'gray';
  };

  const getAgentColor = (agent) => {
    const colors = {
      'DESIGN_LEAD': 'blue',
      'UX_RESEARCH': 'green',
      'PROJECT_MANAGER': 'purple',
      'UX_DESIGN': 'teal',
      'UI_DESIGN': 'pink'
    };
    return colors[agent] || 'gray';
  };

  return (
    <Wrapper>
      <Box width="100%" mx="auto" bg={bgColor} p={6}>
        <VStack align="flex-start" spacing={6} width="100%">
          <Box>
            <Heading size="xl" mb={2}>
              Workflows Registry
            </Heading>
            <Text color="gray.600">
              Manage and configure all available workflows and their execution parameters
            </Text>
          </Box>

          <SimpleGrid columns={4} spacing={4} width="100%">
            <Card>
              <CardBody textAlign="center">
                <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                  {workflows.length}
                </Text>
                <Text fontSize="sm" color="gray.600">Total Workflows</Text>
              </CardBody>
            </Card>
            <Card>
              <CardBody textAlign="center">
                <Text fontSize="2xl" fontWeight="bold" color="green.500">
                  {workflows.filter(w => w.service).length}
                </Text>
                <Text fontSize="sm" color="gray.600">Active Workflows</Text>
              </CardBody>
            </Card>
            <Card>
              <CardBody textAlign="center">
                <Text fontSize="2xl" fontWeight="bold" color="purple.500">
                  {workflows.reduce((acc, w) => acc + (w.agents?.length || 0), 0)}
                </Text>
                <Text fontSize="sm" color="gray.600">Total Agents</Text>
              </CardBody>
            </Card>
            <Card>
              <CardBody textAlign="center">
                <Text fontSize="2xl" fontWeight="bold" color="orange.500">
                  {workflows.reduce((acc, w) => acc + (w.uiComponents?.enabledFeatures?.length || 0), 0)}
                </Text>
                <Text fontSize="sm" color="gray.600">UI Features</Text>
              </CardBody>
            </Card>
          </SimpleGrid>

          <Accordion allowToggle width="100%">
            {workflows.map((workflow) => (
              <AccordionItem key={workflow.id}>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <HStack>
                      <Tag colorScheme={getWorkflowTypeColor(workflow.id)} size="sm">
                        {workflow.id}
                      </Tag>
                      <Heading size="md">{workflow.name}</Heading>
                      <Badge colorScheme={workflow.service ? "green" : "gray"}>
                        {workflow.service ? "Active" : "View Only"}
                      </Badge>
                      <Badge colorScheme="blue" variant="outline">
                        {workflow.estimatedDuration}
                      </Badge>
                    </HStack>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <VStack spacing={6} width="100%">
                    {/* Basic Information - Top Row */}
                    <Card border="1px" borderColor={borderColor} width="100%">
                      <CardBody>
                        <VStack align="start" spacing={4}>
                          <Heading size="md" color="blue.600">📋 Basic Information</Heading>

                          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} width="100%">
                            <Box>
                              <Text fontSize="sm" fontWeight="bold" mb={1} color="gray.700">Description:</Text>
                              <Text fontSize="sm" color="gray.600">{workflow.description}</Text>
                            </Box>

                            <Box>
                              <Text fontSize="sm" fontWeight="bold" mb={1} color="gray.700">Registration Date:</Text>
                              <Text fontSize="sm" color="gray.600">
                                {new Date(workflow.registered).toLocaleDateString()}
                              </Text>
                            </Box>

                            {workflow.service && (
                              <Box>
                                <Text fontSize="sm" fontWeight="bold" mb={1} color="gray.700">Service:</Text>
                                <Badge colorScheme="green">Active Service</Badge>
                              </Box>
                            )}

                            <Box>
                              <Text fontSize="sm" fontWeight="bold" mb={1} color="gray.700">Duration:</Text>
                              <Badge colorScheme="blue" variant="outline">{workflow.estimatedDuration}</Badge>
                            </Box>
                          </SimpleGrid>

                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} width="100%">
                            {workflow.agents && workflow.agents.length > 0 && (
                              <Box>
                                <Text fontSize="sm" fontWeight="bold" mb={2} color="gray.700">Agents:</Text>
                                <Flex wrap="wrap" gap={2}>
                                  {workflow.agents.map((agent) => (
                                    <Tag key={agent} size="sm" colorScheme={getAgentColor(agent)}>
                                      <TagLabel>{agent}</TagLabel>
                                    </Tag>
                                  ))}
                                </Flex>
                              </Box>
                            )}

                            {workflow.requiredData && (
                              <Box>
                                <Text fontSize="sm" fontWeight="bold" mb={2} color="gray.700">Required Data:</Text>
                                <List spacing={1}>
                                  {workflow.requiredData.map((data) => (
                                    <ListItem key={data} fontSize="sm">
                                      <ListIcon as={CheckCircleIcon} color="green.500" />
                                      {data}
                                    </ListItem>
                                  ))}
                                </List>
                              </Box>
                            )}
                          </SimpleGrid>
                        </VStack>
                      </CardBody>
                    </Card>

                    {/* Workflow Execution Flow - Second Row */}
                    {workflow.id === 'design_strategy' && workflow.service && (
                      <Card border="1px" borderColor={borderColor} width="100%">
                        <CardBody>
                          <VStack align="start" spacing={4}>
                            <Heading size="md" color="purple.600">⚡ Workflow Execution Flow</Heading>
                            <Text fontSize="sm" color="gray.500">
                              Complete execution flow with API calls and component interactions
                            </Text>
                            <Text fontSize="sm" color="gray.600" mb={2}>
                              This workflow demonstrates the complete strategy creation process with dynamic agent selection, 
                              mission routing, and AI reasoning tools execution.
                            </Text>

                            {/* Detailed Step Flow */}
                            <VStack spacing={4} align="start" width="100%">
                              {(() => {
                                // Define the complete workflow execution details
                                const executionFlow = [
                                  {
                                    step: 1,
                                    title: "Analyzing your brief",
                                    agent: "Design Lead AI Agent",
                                    mission: "Strategic Planning",
                                    apiCalls: ["/api/autonomous-ai/autonomous-analysis"],
                                    method: "POST",
                                    requestData: ["design_challenge"],
                                    responseData: ["short_summary", "problem_statement", "goal_statement", "objectives_statement", "deliverables_statement", "metrics_statement"],
                                    serviceMethod: "analyzeStep(designChallenge)",
                                    duration: "~2-3 seconds"
                                  },
                                  {
                                    step: 2,
                                    title: "Setting objectives",
                                    agent: "Design Lead AI Agent",
                                    mission: "Strategic Planning",
                                    apiCalls: ["/api/ai/generate-goal"],
                                    method: "POST",
                                    requestData: ["design_challenge"],
                                    responseData: ["goal_statement", "objectives", "success_metrics"],
                                    serviceMethod: "objectivesStep(designChallenge)",
                                    duration: "~2-3 seconds"
                                  },
                                  {
                                    step: 3,
                                    title: "Selecting approach",
                                    agent: "UX Research Lead AI Agent",
                                    mission: "Research & Insights",
                                    apiCalls: ["/api/ai/compute-similarity-scores"],
                                    method: "POST",
                                    requestData: ["design_challenge"],
                                    responseData: ["programs", "tools", "similarity_scores", "lemmatized_challenge"],
                                    serviceMethod: "programsStep(designChallenge, programsOfWork, methods, tools, deliverables)",
                                    algorithms: ["String similarity matching", "Fuzzy keyword matching", "Complexity detection", "Urgency analysis"],
                                    dataProcessing: ["Filter relevant tools", "Calculate program scores", "Build program sequence", "Assign tools to stages"],
                                    duration: "~3-5 seconds"
                                  },
                                  {
                                    step: 4,
                                    title: "Creating roadmap",
                                    agent: "Project Manager AI Agent",
                                    mission: "Project Coordination",
                                    apiCalls: ["Internal processing"],
                                    method: "N/A",
                                    requestData: ["program_sequence", "tool_durations"],
                                    responseData: ["timeline", "total_duration", "phases"],
                                    serviceMethod: "timelineStep()",
                                    duration: "~1 second"
                                  },
                                  {
                                    step: 5,
                                    title: "Finalizing strategy",
                                    agent: "Design Lead AI Agent",
                                    mission: "Strategic Planning",
                                    apiCalls: ["Internal compilation"],
                                    method: "N/A",
                                    requestData: ["all_workflow_results"],
                                    responseData: ["compiled_strategy", "validation_status", "ready_for_save"],
                                    serviceMethod: "finalizeStep() + compileWorkflowResults()",
                                    validations: ["Program sequence validation", "Data completeness check", "Duplication detection"],
                                    duration: "~1-2 seconds"
                                  }
                                ];

                                return executionFlow.map((flow, index) => (
                                  <Box key={flow.step} width="100%" p={4} border="1px" borderColor="purple.200" borderRadius="lg" bg="purple.50">
                                    <VStack align="start" spacing={3}>
                                      {/* Step Header */}
                                      <HStack spacing={3} width="100%">
                                        <Badge 
                                          colorScheme="purple" 
                                          variant="solid" 
                                          borderRadius="full"
                                          minW="32px"
                                          h="32px"
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="center"
                                          fontSize="sm"
                                          fontWeight="bold"
                                        >
                                          {flow.step}
                                        </Badge>
                                        <VStack align="start" spacing={1} flex="1">
                                          <Text fontSize="md" fontWeight="bold" color="purple.800">
                                            {flow.title}
                                          </Text>
                                          <HStack spacing={2}>
                                            <Tag size="sm" colorScheme="blue">
                                              {flow.agent}
                                            </Tag>
                                            <Tag size="sm" variant="outline" colorScheme="purple">
                                              {flow.mission}
                                            </Tag>
                                            <Tag size="sm" variant="outline" colorScheme="green">
                                              {flow.duration}
                                            </Tag>
                                          </HStack>
                                        </VStack>
                                      </HStack>

                                      {/* Technical Details Grid */}
                                      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} width="100%">
                                        {/* Left Column - API & Service Info */}
                                        <VStack align="start" spacing={3}>
                                          <Box>
                                            <Text fontSize="sm" color="gray.600">
                                              Mission routing selects {flow.agent} → executes {flow.step === 1 ? 'analyze_brief' : flow.step === 2 ? 'generate_goal' : flow.step === 3 ? 'compute_similarity' : 'internal_processing'} AI reasoning tool
                                            </Text>
                                            <Box mt={2} p={2} bg="purple.50" borderRadius="md">
                                              <Text fontSize="xs" fontWeight="bold" color="purple.800">Dynamic Routing:</Text>
                                              <Text fontSize="xs" color="purple.700">POST /api/agents/mission-chat → routes to {flow.step <= 2 ? 'DESIGN_LEAD' : flow.step === 3 ? 'UX_RESEARCH' : 'PROJECT_MANAGER'}</Text>
                                              <Text fontSize="xs" fontWeight="bold" color="purple.800">AI Tool Execution:</Text>
                                              <Text fontSize="xs" color="purple.700">{flow.apiCalls[0]} ({flow.step === 1 ? 'analyze_brief' : flow.step === 2 ? 'generate_goal' : flow.step === 3 ? 'compute_similarity' : 'internal'} tool)</Text>
                                              <Text fontSize="xs" color="purple.700">Service Method: {flow.serviceMethod}</Text>
                                            </Box>
                                          </Box>

                                          <Box>
                                            <Text fontSize="sm" fontWeight="bold" color="orange.600" mb={1}>Service Method:</Text>
                                            <Text fontSize="sm" fontFamily="mono" color="gray.700" bg="white" px={2} py={1} borderRadius="md">
                                              {flow.serviceMethod}
                                            </Text>
                                          </Box>
                                        </VStack>

                                        {/* Right Column - Data Flow */}
                                        <VStack align="start" spacing={3}>
                                          <Box>
                                            <Text fontSize="sm" fontWeight="bold" color="blue.600" mb={1}>Request Data:</Text>
                                            <Flex wrap="wrap" gap={1}>
                                              {flow.requestData.map((data, i) => (
                                                <Badge key={i} size="sm" colorScheme="blue" variant="outline">
                                                  {data}
                                                </Badge>
                                              ))}
                                            </Flex>
                                          </Box>

                                          <Box>
                                            <Text fontSize="sm" fontWeight="bold" color="green.600" mb={1}>Response Data:</Text>
                                            <Flex wrap="wrap" gap={1}>
                                              {flow.responseData.map((data, i) => (
                                                <Badge key={i} size="sm" colorScheme="green" variant="outline">
                                                  {data}
                                                </Badge>
                                              ))}
                                            </Flex>
                                          </Box>
                                        </VStack>
                                      </SimpleGrid>

                                      {/* Additional Technical Details */}
                                      {(flow.algorithms || flow.dataProcessing || flow.validations) && (
                                        <VStack align="start" spacing={2} width="100%">
                                          {flow.algorithms && (
                                            <Box>
                                              <Text fontSize="sm" fontWeight="bold" color="purple.600" mb={1}>Algorithms Used:</Text>
                                              <Flex wrap="wrap" gap={1}>
                                                {flow.algorithms.map((algo, i) => (
                                                  <Badge key={i} size="sm" colorScheme="purple" variant="outline">
                                                    {algo}
                                                  </Badge>
                                                ))}
                                              </Flex>
                                            </Box>
                                          )}

                                          {flow.dataProcessing && (
                                            <Box>
                                              <Text fontSize="sm" fontWeight="bold" color="teal.600" mb={1}>Data Processing:</Text>
                                              <Flex wrap="wrap" gap={1}>
                                                {flow.dataProcessing.map((process, i) => (
                                                  <Badge key={i} size="sm" colorScheme="teal" variant="outline">
                                                    {process}
                                                  </Badge>
                                                ))}
                                              </Flex>
                                            </Box>
                                          )}

                                          {flow.validations && (
                                            <Box>
                                              <Text fontSize="sm" fontWeight="bold" color="red.600" mb={1}>Validations:</Text>
                                              <Flex wrap="wrap" gap={1}>
                                                {flow.validations.map((validation, i) => (
                                                  <Badge key={i} size="sm" colorScheme="red" variant="outline">
                                                    {validation}
                                                  </Badge>
                                                ))}
                                              </Flex>
                                            </Box>
                                          )}
                                        </VStack>
                                      )}
                                    </VStack>
                                  </Box>
                                ));
                              })()}
                            </VStack>

                            {/* Workflow Manager Integration */}
                            <Box mt={4} p={4} bg="gray.100" borderRadius="lg" width="100%">
                              <Text fontSize="sm" fontWeight="bold" color="gray.800" mb={3}>
                                🔧 Workflow Management Layer:
                              </Text>
                              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                <VStack align="start" spacing={2}>
                                  <Text fontSize="sm" color="gray.700">
                                    📋 <strong>StrategyWorkflowManager:</strong> Orchestrates execution, manages callbacks, handles step progression
                                  </Text>
                                  <Text fontSize="sm" color="gray.700">
                                    🔧 <strong>ComponentActionHandler:</strong> Receives completed results, triggers save operation, updates UI state
                                  </Text>
                                </VStack>
                                <VStack align="start" spacing={2}>
                                  <Text fontSize="sm" color="gray.700">
                                    💬 <strong>Chat Integration:</strong> Real-time progress updates, agent messages, workflow status notifications
                                  </Text>
                                  <Text fontSize="sm" color="gray.700">
                                    💾 <strong>Database Save:</strong> Stores complete strategy with programs, tools, timeline, and conversation history
                                  </Text>
                                </VStack>
                              </SimpleGrid>
                            </Box>
                          </VStack>
                        </CardBody>
                      </Card>
                    )}

                    {workflow.id !== 'design_strategy' && workflow.service && (
                      <Card border="1px" borderColor={borderColor} width="100%">
                        <CardBody>
                          <VStack align="start" spacing={2}>
                            <Heading size="md" color="purple.600">⚡ Workflow Execution Flow</Heading>
                            <Text fontSize="sm" color="gray.500" fontStyle="italic">
                              Detailed execution flow not yet documented for this workflow
                            </Text>
                          </VStack>
                        </CardBody>
                      </Card>
                    )}

                    {!workflow.service && (
                      <Card border="1px" borderColor={borderColor} width="100%">
                        <CardBody>
                          <VStack align="start" spacing={2}>
                            <Heading size="md" color="gray.500">👁️ View-Only Workflow</Heading>
                            <Text fontSize="sm" color="gray.500" fontStyle="italic">
                              This workflow is for viewing existing data only - no execution flow
                            </Text>
                          </VStack>
                        </CardBody>
                      </Card>
                    )}

                    {/* UI Component Mapping & Configuration - Third Row */}
                    <Card border="1px" borderColor={borderColor} width="100%">
                      <CardBody>
                        <VStack align="start" spacing={6}>
                          <Heading size="md" color="green.600">🎨 UI Component Mapping & Configuration</Heading>

                          {/* View Components Mapping */}
                          {workflow.uiComponents?.views && (
                            <Box width="100%">
                              <Text fontSize="md" fontWeight="bold" mb={4} color="gray.700">View Components Mapping:</Text>
                              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                                {(() => {
                                  const viewComponentMapping = {
                                    "strategy": {
                                      component: "DesignStrategyVision.js",
                                      path: "src/components/DesignStrategyVision.js",
                                      config: "strategyButtonConfig",
                                      configSource: "src/config/navigationConfig.js",
                                      capabilities: ["analyze_brief", "generate_goal", "save_plan"],
                                      description: "Main strategy overview with goals and objectives"
                                    },
                                    "stepByStep": {
                                      component: "StepByStepView.js", 
                                      path: "src/components/StepByStepView.js",
                                      config: "stepByStepButtonConfig",
                                      configSource: "src/config/navigationConfig.js",
                                      capabilities: ["drag_drop_tools", "program_management", "ai_generated_content"],
                                      description: "Detailed program execution with drag-and-drop tools"
                                    },
                                    "slick": {
                                      component: "SlickView.js",
                                      path: "src/components/SlickView.js", 
                                      config: "slickButtonConfig",
                                      configSource: "src/config/navigationConfig.js",
                                      capabilities: ["visual_summary", "program_overview"],
                                      description: "Compact visual overview of strategy"
                                    },
                                    "calendar": {
                                      component: "CalendarView.js",
                                      path: "src/components/CalendarView.js",
                                      config: "calendarButtonConfig", 
                                      configSource: "src/config/navigationConfig.js",
                                      capabilities: ["timeline_view", "milestone_tracking"],
                                      description: "Timeline and calendar view of activities"
                                    }
                                  };

                                  return workflow.uiComponents.views.map((view) => {
                                    const mapping = viewComponentMapping[view];
                                    const isDefault = view === workflow.uiComponents.defaultView;

                                    return (
                                      <Box key={view} p={4} border="1px" borderColor="green.200" borderRadius="lg" bg="green.50">
                                        <VStack align="start" spacing={3}>
                                          <HStack spacing={2} mb={2}>
                                            <Badge colorScheme={isDefault ? "green" : "gray"} variant={isDefault ? "solid" : "outline"} size="lg">
                                              {isDefault && <StarIcon mr={1} boxSize={3} />}
                                              {view}
                                            </Badge>
                                            {isDefault && <Badge size="sm" colorScheme="green">DEFAULT</Badge>}
                                          </HStack>

                                          {mapping && (
                                            <VStack align="start" spacing={2} width="100%">
                                              <HStack spacing={2}>
                                                <Text fontSize="sm" fontWeight="bold" color="purple.600">Component:</Text>
                                                <Text fontSize="sm" fontFamily="mono" bg="white" px={2} py={1} borderRadius="md">{mapping.component}</Text>
                                              </HStack>
                                              <HStack spacing={2}>
                                                <Text fontSize="sm" fontWeight="bold" color="blue.600">Path:</Text>
                                                <Text fontSize="sm" fontFamily="mono" color="gray.600" bg="white" px={2} py={1} borderRadius="md">{mapping.path}</Text>
                                              </HStack>
                                              <HStack spacing={2}>
                                                <Text fontSize="sm" fontWeight="bold" color="green.600">Config:</Text>
                                                <Text fontSize="sm" fontFamily="mono" bg="white" px={2} py={1} borderRadius="md">{mapping.config}</Text>
                                              </HStack>
                                              <Text fontSize="sm" color="gray.700" bg="white" p={2} borderRadius="md" fontStyle="italic">
                                                {mapping.description}
                                              </Text>
                                              <Box>
                                                <Text fontSize="sm" fontWeight="bold" color="orange.600" mb={1}>Capabilities:</Text>
                                                <Flex wrap="wrap" gap={1}>
                                                  {mapping.capabilities.map((cap, i) => (
                                                    <Badge key={i} size="sm" colorScheme="orange" variant="outline">
                                                      {cap}
                                                    </Badge>
                                                  ))}
                                                </Flex>
                                              </Box>
                                            </VStack>
                                          )}
                                        </VStack>
                                      </Box>
                                    );
                                  });
                                })()}
                              </SimpleGrid>
                            </Box>
                          )}

                          {/* Configuration Sources */}
                          <Box width="100%">
                            <Text fontSize="md" fontWeight="bold" mb={4} color="gray.700">Configuration Sources:</Text>
                            <VStack spacing={4} align="start">
                              <Box p={4} bg="blue.50" borderRadius="lg" width="100%" border="1px" borderColor="blue.200">
                                <Text fontSize="sm" fontWeight="bold" color="blue.800" mb={2}>Workflow Registry Configuration:</Text>
                                <Text fontSize="sm" color="blue.700" fontFamily="mono" bg="white" p={2} borderRadius="md" mb={2}>
                                  src/services/WorkflowRegistry.js → registerWorkflow("{workflow.id}")
                                </Text>
                                <HStack spacing={2} wrap="wrap">
                                  <Text fontSize="sm" color="blue.600" fontWeight="bold">Defines:</Text>
                                  <Badge size="sm" colorScheme="blue" variant="outline">views</Badge>
                                  <Badge size="sm" colorScheme="blue" variant="outline">defaultView</Badge>
                                  <Badge size="sm" colorScheme="blue" variant="outline">enabledFeatures</Badge>
                                  <Badge size="sm" colorScheme="blue" variant="outline">chatPanel</Badge>
                                  <Badge size="sm" colorScheme="blue" variant="outline">navbar</Badge>
                                </HStack>
                              </Box>

                              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} width="100%">
                                <Box p={4} bg="green.50" borderRadius="lg" border="1px" borderColor="green.200">
                                  <Text fontSize="sm" fontWeight="bold" color="green.800" mb={2}>Navigation Configuration:</Text>
                                  <Text fontSize="sm" color="green.700" fontFamily="mono" bg="white" p={2} borderRadius="md" mb={2}>
                                    src/config/navigationConfig.js → navigationButtons[]
                                  </Text>
                                  <HStack spacing={2} wrap="wrap">
                                    <Text fontSize="sm" color="green.600" fontWeight="bold">Defines:</Text>
                                    <Badge size="sm" colorScheme="green" variant="outline">button labels</Badge>
                                    <Badge size="sm" colorScheme="green" variant="outline">icons</Badge>
                                    <Badge size="sm" colorScheme="green" variant="outline">tooltips</Badge>
                                  </HStack>
                                </Box>

                                <Box p={4} bg="purple.50" borderRadius="lg" border="1px" borderColor="purple.200">
                                  <Text fontSize="sm" fontWeight="bold" color="purple.800" mb={2}>Core Features Registry:</Text>
                                  <Text fontSize="sm" color="purple.700" fontFamily="mono" bg="white" p={2} borderRadius="md" mb={2}>
                                    routes/core-features-registry.js → FEATURES_REGISTRY
                                  </Text>
                                  <HStack spacing={2} wrap="wrap">
                                    <Text fontSize="sm" color="purple.600" fontWeight="bold">Defines:</Text>
                                    <Badge size="sm" colorScheme="purple" variant="outline">component paths</Badge>
                                    <Badge size="sm" colorScheme="purple" variant="outline">capabilities</Badge>
                                    <Badge size="sm" colorScheme="purple" variant="outline">AI capabilities</Badge>
                                  </HStack>
                                </Box>
                              </SimpleGrid>
                            </VStack>
                          </Box>

                          {/* Current UI Configuration */}
                          <Box width="100%">
                            <Text fontSize="md" fontWeight="bold" mb={4} color="gray.700">Current UI Configuration:</Text>

                            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                              {workflow.uiComponents?.chatPanel && (
                                <Box p={4} border="1px" borderColor="teal.200" borderRadius="lg" bg="teal.50">
                                  <Text fontSize="sm" fontWeight="bold" color="teal.700" mb={3}>💬 Chat Panel Configuration:</Text>
                                  <VStack spacing={2} align="start">
                                    <HStack>
                                      <Text fontSize="sm" fontWeight="bold">Type:</Text>
                                      <Badge size="sm" colorScheme="teal">{workflow.uiComponents.chatPanel.type}</Badge>
                                    </HStack>
                                    <HStack>
                                      <Text fontSize="sm" fontWeight="bold">Progress:</Text>
                                      <Badge size="sm" colorScheme={workflow.uiComponents.chatPanel.showProgress ? "green" : "red"}>
                                        {workflow.uiComponents.chatPanel.showProgress ? "Yes" : "No"}
                                      </Badge>
                                    </HStack>
                                    <HStack>
                                      <Text fontSize="sm" fontWeight="bold">Interaction:</Text>
                                      <Badge size="sm" colorScheme={workflow.uiComponents.chatPanel.allowInteraction ? "green" : "red"}>
                                        {workflow.uiComponents.chatPanel.allowInteraction ? "Yes" : "No"}
                                      </Badge>
                                    </HStack>
                                  </VStack>
                                </Box>
                              )}

                              {workflow.uiComponents?.navbar && (
                                <Box p={4} border="1px" borderColor="orange.200" borderRadius="lg" bg="orange.50">
                                  <Text fontSize="sm" fontWeight="bold" color="orange.700" mb={3}>🧭 Navbar Configuration:</Text>
                                  <Flex wrap="wrap" gap={2}>
                                    {workflow.uiComponents.navbar.showSaveButton && (
                                      <Badge size="sm" colorScheme="green">Save Button</Badge>
                                    )}
                                    {workflow.uiComponents.navbar.showViewTabs && (
                                      <Badge size="sm" colorScheme="blue">View Tabs</Badge>
                                    )}
                                    {workflow.uiComponents.navbar.showDurationTag && (
                                      <Badge size="sm" colorScheme="purple">Duration Tag</Badge>
                                    )}
                                    {workflow.uiComponents.navbar.compactMode && (
                                      <Badge size="sm" colorScheme="orange">Compact Mode</Badge>
                                    )}
                                  </Flex>
                                </Box>
                              )}
                            </SimpleGrid>
                          </Box>
                        </VStack>
                      </CardBody>
                    </Card>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </VStack>
      </Box>
    </Wrapper>
  );
};

export default AdminWorkflows;